import { z } from "zod";
import type { AppTranslateFn } from "@/hooks/use-app-translation";

export const LATITUDE_MIN = -90;
export const LATITUDE_MAX = 90;
export const LONGITUDE_MIN = -180;
export const LONGITUDE_MAX = 180;

/** Figma Enter Coordinates out-of-range hint (latitude). */
export const LATITUDE_VALUE_RANGE_MESSAGE =
	"Value range is -90.000000 to 90.000000.";

/** Figma Enter Coordinates out-of-range hint (longitude). */
export const LONGITUDE_VALUE_RANGE_MESSAGE =
	"Value range is -180.000000 to 180.000000.";

/** Figma Enter Coordinates required hint. */
export const COORDINATE_REQUIRED_MESSAGE = "This field is required.";

export const CoordinateFieldError = {
	Required: "required",
	Range: "range",
} as const;

export type CoordinateFieldError =
	(typeof CoordinateFieldError)[keyof typeof CoordinateFieldError];

/**
 * Parses a latitude/longitude field. Empty, lone signs, and non-finite values
 * return null (not a completed number).
 */
export function parseCoordinateNumber(raw: string): number | null {
	const trimmed = raw.trim();
	if (
		trimmed === "" ||
		trimmed === "-" ||
		trimmed === "." ||
		trimmed === "-."
	) {
		return null;
	}

	const value = Number(trimmed);
	if (!Number.isFinite(value)) {
		return null;
	}

	return value;
}

/**
 * Zod bounds for a finished latitude number ([-90, 90]).
 */
export const latitudeNumberSchema = z
	.number()
	.min(LATITUDE_MIN)
	.max(LATITUDE_MAX);

/**
 * Zod bounds for a finished longitude number ([-180, 180]).
 */
export const longitudeNumberSchema = z
	.number()
	.min(LONGITUDE_MIN)
	.max(LONGITUDE_MAX);

/**
 * Parsed point after Enter Coordinates validation succeeds.
 */
export const setLocationCoordinatePairSchema = z.object({
	latitude: latitudeNumberSchema,
	longitude: longitudeNumberSchema,
});

export type SetLocationCoordinatePair = z.infer<
	typeof setLocationCoordinatePairSchema
>;

/**
 * Authenticated i18n returns "" for missing keys; Zod 4 then replaces an empty
 * custom issue message with "Invalid input". Prefer a non-empty localized
 * string when the API has the key, otherwise the Figma English copy.
 */
export function resolveCoordinateMessage(
	translated: string,
	figmaFallback: string,
): string {
	return translated.trim().length > 0 ? translated : figmaFallback;
}

function createCoordinateAxisSchema(
	t: AppTranslateFn,
	bounds: z.ZodNumber,
	rangeMessageKey:
		| "main.map.setLocation.latitudeValueRange"
		| "main.map.setLocation.longitudeValueRange",
	rangeFallback: string,
) {
	const rangeMessage = resolveCoordinateMessage(
		t(rangeMessageKey),
		rangeFallback,
	);
	const requiredMessage = resolveCoordinateMessage(
		t("common.validation.required"),
		COORDINATE_REQUIRED_MESSAGE,
	);

	return z
		.string()
		.superRefine((raw, ctx) => {
			const trimmed = raw.trim();
			if (trimmed === "") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: requiredMessage,
					params: { coordinateError: CoordinateFieldError.Required },
				});
				return;
			}

			const value = parseCoordinateNumber(raw);
			if (value === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: rangeMessage,
					params: { coordinateError: CoordinateFieldError.Range },
				});
				return;
			}

			const boundsResult = bounds.safeParse(value);
			if (!boundsResult.success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: rangeMessage,
					params: { coordinateError: CoordinateFieldError.Range },
				});
			}
		})
		.transform((raw) => parseCoordinateNumber(raw) as number);
}

/**
 * Enter Coordinates Zod schema: both fields required; lat ∈ [-90, 90],
 * lng ∈ [-180, 180]. Transforms string drafts to numbers on success.
 */
export function createSetLocationCoordinatesSchema(t: AppTranslateFn) {
	return z.object({
		latitude: createCoordinateAxisSchema(
			t,
			latitudeNumberSchema,
			"main.map.setLocation.latitudeValueRange",
			LATITUDE_VALUE_RANGE_MESSAGE,
		),
		longitude: createCoordinateAxisSchema(
			t,
			longitudeNumberSchema,
			"main.map.setLocation.longitudeValueRange",
			LONGITUDE_VALUE_RANGE_MESSAGE,
		),
	});
}

export type SetLocationCoordinatesSchema = ReturnType<
	typeof createSetLocationCoordinatesSchema
>;

/**
 * Validates latitude in [-90, 90]. Empty is required when `required` is true.
 */
export function validateLatitude(
	raw: string,
	options: { required: boolean } = { required: true },
): CoordinateFieldError | undefined {
	const trimmed = raw.trim();
	if (trimmed === "") {
		return options.required ? CoordinateFieldError.Required : undefined;
	}

	const value = parseCoordinateNumber(raw);
	if (value === null || !latitudeNumberSchema.safeParse(value).success) {
		return CoordinateFieldError.Range;
	}

	return undefined;
}

/**
 * Validates longitude in [-180, 180]. Empty is required when `required` is true.
 */
export function validateLongitude(
	raw: string,
	options: { required: boolean } = { required: true },
): CoordinateFieldError | undefined {
	const trimmed = raw.trim();
	if (trimmed === "") {
		return options.required ? CoordinateFieldError.Required : undefined;
	}

	const value = parseCoordinateNumber(raw);
	if (value === null || !longitudeNumberSchema.safeParse(value).success) {
		return CoordinateFieldError.Range;
	}

	return undefined;
}

export interface CoordinatePairResult {
	ok: boolean;
	latitude?: number;
	longitude?: number;
	latitudeError?: CoordinateFieldError;
	longitudeError?: CoordinateFieldError;
}

function coordinateErrorFromZodIssue(
	issue: z.ZodIssue | undefined,
): CoordinateFieldError | undefined {
	if (issue == null) return undefined;
	const fromParams = (
		issue as z.ZodIssue & {
			params?: { coordinateError?: CoordinateFieldError };
		}
	).params?.coordinateError;
	if (fromParams != null) return fromParams;
	return CoordinateFieldError.Range;
}

/**
 * Both fields are required. Returns parsed numbers only when both pass Zod
 * latitude/longitude bounds.
 */
export function parseCoordinatePair(
	latitude: string,
	longitude: string,
): CoordinatePairResult {
	const result = createSetLocationCoordinatesSchema((key) => key).safeParse({
		latitude,
		longitude,
	});

	if (result.success) {
		return {
			ok: true,
			latitude: result.data.latitude,
			longitude: result.data.longitude,
		};
	}

	const fieldErrors = result.error.flatten().fieldErrors;
	const latitudeIssue = result.error.issues.find((issue) =>
		issue.path.includes("latitude"),
	);
	const longitudeIssue = result.error.issues.find((issue) =>
		issue.path.includes("longitude"),
	);

	return {
		ok: false,
		latitudeError:
			fieldErrors.latitude != null
				? coordinateErrorFromZodIssue(latitudeIssue)
				: undefined,
		longitudeError:
			fieldErrors.longitude != null
				? coordinateErrorFromZodIssue(longitudeIssue)
				: undefined,
	};
}

/**
 * Formats a point as `lat, lng` with six decimal places (Figma supporting line).
 */
export function formatGeocodeCoordinates(
	latitude: number,
	longitude: number,
): string {
	return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}
